import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API_BASE } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { LygoLogo } from '@/components/LygoLogo';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || t('auth.login.error'));
      }

      const data = await res.json();
      login(data.token, data.email);
      navigate('/overview');
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-8 bg-[radial-gradient(circle_at_15%_15%,hsl(193_62%_88%),transparent_34%),radial-gradient(circle_at_84%_88%,hsl(38_85%_90%),transparent_30%)]">
      <Card className="w-full max-w-[460px] border-primary/15 shadow-[0_14px_40px_rgba(15,23,42,0.12)]">
        <CardHeader className="space-y-1">
          <LygoLogo />
          <CardTitle className="text-2xl font-heading pt-1">{t('auth.brandName')}</CardTitle>
          <CardDescription>{t('auth.login.title')}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.passwordLabel')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('auth.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full">{t('auth.login.submit')}</Button>
          </form>
          <div className="mt-4 text-center text-sm space-y-1">
            <p><Link to="/forgot-password" className="text-primary hover:underline">{t('auth.login.forgotPassword')}</Link></p>
            <p className="text-muted-foreground">
              {t('auth.login.noAccount')}{' '}
              <Link to="/register" className="text-primary hover:underline">{t('auth.login.registerLink')}</Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
